import { ApiError, type ApiErrorEnvelope } from './client';

/**
 * ============================================================================
 * FETCHING A FILE THE SERVER OFFERED, SO THAT ITS REFUSALS ARE READABLE
 * ============================================================================
 *
 * `GET /pa/documents/{id}/file` can refuse in three ways the subscriber is entitled to
 * understand, and they are not the same event:
 *
 *   `404 pa.not_found`           the row is not his, or its type is no longer open to the
 *                                cabinet (D211's second door);
 *   `503 pa.storage_unavailable` THE DOCUMENT IS HIS AND WE CANNOT PRODUCE IT. The server
 *                                says so deliberately — «the reason he cannot have it is
 *                                ours» — and this is not a hypothetical: dropping the file
 *                                config at the composition root makes EVERY download on
 *                                EVERY installation answer 503 while the list still renders
 *                                perfectly (the W1/W2 mutation lens reproduced exactly that,
 *                                with the whole Go suite green);
 *   `401 pa.unauthenticated`     the session slid past its idle TTL while he was reading.
 *
 * A plain anchor hands all three to the BROWSER. With `download` set, Chrome reports
 * «Failed»; without it, the portal page is replaced by a raw JSON error envelope. Either
 * way the server's own Russian sentence never reaches him, and `errorText` — the one place
 * a failure becomes a sentence in this app — is bypassed.
 *
 * # What this does, and what it deliberately keeps
 *
 * The «Скачать» control stays a REAL ANCHOR with the server's own `download_url` in its
 * `href`: a long-press «Save link», a middle-click and a copied address all keep working
 * on the native path, and if this module were removed tomorrow the link would still be a
 * link. What the click handler adds is the ability to SEE the outcome — it fetches the
 * same URL with the session cookie, and on a refusal throws the same `ApiError` shape the
 * rest of this app already renders through `errorText`.
 *
 * ⚠ AND IT FALLS BACK TO THE NATIVE PATH RATHER THAN FAILING. `apps/portal/README.md`
 * records the original reason for the plain anchor: a blob download «would break the one
 * thing that must work on a phone — Save to Files». That claim was never measured (and is
 * not measured here either — said plainly rather than glossed), so this module does not
 * bet the happy path on it: when `URL.createObjectURL` is unavailable it navigates to the
 * URL exactly as the anchor would have, and a browser that dislikes blob downloads
 * therefore degrades to today's behaviour instead of to a broken button.
 */

/**
 * fetchDocument retrieves `url` with the session cookie and hands back the bytes.
 *
 * ⚠ THE URL IS THE SERVER'S OWN, PASSED IN. Nothing here builds a document path from an
 * id — that would be a client deciding which ids are reachable, which is the enumeration
 * the flat 404 exists to prevent, and `DocumentsScreen.test.tsx`'s source sweep fails on
 * any component that names `/pa/documents` at all.
 */
async function fetchDocument(url: string): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'same-origin', headers: { Accept: '*/*' } });
  } catch {
    throw new ApiError(0, { code: 'network_error', message: 'network_error' }, true);
  }
  if (!res.ok) {
    let envelope: ApiErrorEnvelope = { code: `http_${res.status}`, message: `HTTP ${res.status}` };
    try {
      const body = (await res.json()) as { error?: ApiErrorEnvelope };
      if (body && body.error && typeof body.error.message === 'string') {
        envelope = body.error;
      }
    } catch {
      // no / malformed JSON body — the synthetic envelope above is what errorText needs
      // to tell «the server explained itself» from «something in front of it did not».
    }
    throw new ApiError(res.status, envelope);
  }
  return res.blob();
}

/**
 * downloadFile fetches the file and saves it under `filename`, or THROWS an `ApiError`
 * the caller renders through `errorText`.
 *
 * Resolves when the save has been handed to the browser. It never resolves «successfully»
 * on a refusal, which is the whole point.
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const blob = await fetchDocument(url);

  const maker = typeof URL !== 'undefined' ? URL.createObjectURL : undefined;
  if (typeof maker !== 'function') {
    // No blob URLs in this environment: take the native path, which is what the anchor
    // would have done, rather than swallowing a document the subscriber asked for.
    window.location.assign(url);
    return;
  }

  const objectURL = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectURL;
  // The server names the file (`Content-Disposition`); this is the same name the list row
  // carries, so the saved file matches what he clicked on.
  a.download = filename || '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the NEXT task, not in a `finally`. Revoking a blob URL in the same tick as
  // the click cancels the save in Safari, and a download that silently does not happen is
  // the failure this module exists to make visible.
  setTimeout(() => {
    try {
      URL.revokeObjectURL(objectURL);
    } catch {
      // the document has gone away; the browser reclaims the blob with it
    }
  }, 0);
}
